import React from 'react';
import { useController } from 'react-hook-form';
import { DateTime } from 'luxon';

import styles from './styles.module.scss';
import SelectBox from 'app/components/OptionButtonGroup/SelectBox';
import useSlotInterval from 'app/utils/hooks/scheduling/useSlotInterval';
import useCalendarPreferences from 'app/utils/hooks/useCalendarPreferences';

type PropsDef = {
  name: string;
  control: any;
  timeZone: string;
  dateList: any[];
  customStartTime: string;
  'data-testid'?: string;
  placeholder?: string;
};

const timeToMinutes = (value, timeZone) => {
  const time = DateTime.fromISO(value).setZone(timeZone);

  const minutes = time.hour * 60 + time.minute;

  return minutes;
};

const minutesToTime = (day, timeZone, minutes) => {
  const time = DateTime.fromISO(day).setZone(timeZone);

  return time.startOf('day').plus({minutes}).toISO();
};

// Mirror the label format the slot-grid options use (see calculateHours in
// useSessionTiming), so a synthetic off-grid option reads identically.
const formatMinutesLabel = (minutes: number, timeFormat: '12h' | '24h') => {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;

  if (timeFormat === '24h') {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  }

  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
};

const onSetValue = (field, timeZone, minutes) => {
  const today = field.value === null ? DateTime.now().setZone(timeZone).toISO() : field.value;

  const time = minutesToTime(today, timeZone, minutes);

  field.onChange(time);
};

const disabledValues = (dateList, field, timeZone, customStartTime, minuteStep) => {
  if(field.name === 'start') {
    return dateList;
  }

  let minutes;

  if(customStartTime) {
    minutes = timeToMinutes(customStartTime, timeZone);
  } else {
    minutes = timeToMinutes(field.value, timeZone) - minuteStep;
  }

  return dateList.filter(element => element.value > minutes);
};

const TimeRangeInput = React.forwardRef((props: PropsDef, _ref) => {
  const {
    name,
    control,
    timeZone,
    dateList,
    customStartTime,
    'data-testid': testId,
    placeholder,
  } = props;

  const { step: minuteStep } = useSlotInterval();
  const { preferences } = useCalendarPreferences();

  const { field } = useController({ name, control });
  const hasValue = !!field.value && DateTime.fromISO(field.value).isValid;
  const currentMinutes = hasValue ? timeToMinutes(field.value, timeZone) : null;

  const localDateList = disabledValues(dateList, field, timeZone, customStartTime, minuteStep);

  // A custom-duration end (e.g. +1 min, +50 min) can land between slot-grid
  // options; the grid list won't contain it, so surface it as a synthetic
  // option to display the real time instead of a blank box.
  const matchedOption =
    currentMinutes !== null
      ? localDateList?.find((el) => el.value === currentMinutes) ?? null
      : null;
  const selectedValue =
    matchedOption ??
    (currentMinutes !== null
      ? { value: currentMinutes, label: formatMinutesLabel(currentMinutes, preferences.timeFormat) }
      : null);

  return (
    <div className={styles.container} data-testid={testId}>
      <SelectBox
        options={localDateList}
        value={selectedValue}
        setValue={(dateListElement: {value: number, label: string}) => {
          onSetValue(field, timeZone, dateListElement.value);
        }

        }
        customWidth="100%"
        placeholder={placeholder}
        isSearchable={false}
        data-testid={testId ? `${testId}-dropdown` : undefined}
      />
    </div>
  );
});

export default TimeRangeInput;
